import rawQuestionBank from "../data/question-bank.seed.json";
import type { Difficulty, QuizQuestion } from "./types";

type SeedQuestion = {
  id: string;
  category: string;
  difficulty: string;
  prompt: string;
  options: string[];
  answer: string;
  explanation: string;
  sourceType: string;
  questionType?: string;
};

type FifaPlayer = {
  id: string;
  name: string;
  position: string;
  photoUrl: string | null;
  photoAlt: string;
  teamName: string;
  teamSlug: string;
  sourceType: string;
  sourceUrl: string;
};

type FifaTeam = {
  slug: string;
  name: string;
  squadUrl: string;
  players: FifaPlayer[];
};

type FifaSquads = {
  teams: FifaTeam[];
};

const categoryLabels: Record<string, string> = {
  current_world_cup: "World Cup 2026",
  player_photo_identification: "Players",
  player_team_identification: "Players",
  player_name_identification: "Players",
  player_position_identification: "Positions",
  world_cup_history: "World Cup History",
  football_laws: "Rules",
  tactics_positions: "Tactics"
};

const accentByCategory: Record<string, QuizQuestion["visual"]["accent"]> = {
  current_world_cup: "blue",
  player_photo_identification: "blue",
  player_team_identification: "blue",
  player_name_identification: "blue",
  player_position_identification: "green",
  world_cup_history: "gold",
  football_laws: "green",
  tactics_positions: "green"
};

const difficultyLabels: Record<string, Difficulty> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  expert: "Expert"
};

let cachedQuestionBank: QuizQuestion[] | undefined;

function isProductionReady(question: SeedQuestion): boolean {
  const marker = `${question.sourceType} ${question.prompt} ${question.explanation}`;
  return !/todo|SEED_|TODO_/i.test(marker);
}

function formatCategory(category: string): string {
  return categoryLabels[category] ?? category.replaceAll("_", " ");
}

function formatDifficulty(difficulty: string): Difficulty {
  return difficultyLabels[difficulty] ?? "Medium";
}

function optionSet(correct: string, candidates: string[], offset: number): string[] {
  const unique = candidates.filter((candidate, index, list) => candidate !== correct && list.indexOf(candidate) === index);
  const options = [correct];
  let cursor = offset;

  while (options.length < 4 && unique.length > 0) {
    const next = unique[cursor % unique.length];
    if (!options.includes(next)) {
      options.push(next);
    }
    cursor += 7;
  }

  const shift = offset % options.length;
  return [...options.slice(shift), ...options.slice(0, shift)];
}

function playerOptionSet(correct: FifaPlayer, candidates: FifaPlayer[], offset: number): FifaPlayer[] {
  const unique = candidates.filter(
    (candidate, index, list) =>
      candidate.id !== correct.id &&
      candidate.name !== correct.name &&
      list.findIndex((item) => item.name === candidate.name) === index
  );
  const options = [correct];
  let cursor = offset;

  while (options.length < 4 && unique.length > 0) {
    const next = unique[cursor % unique.length];
    if (!options.some((option) => option.id === next.id || option.name === next.name)) {
      options.push(next);
    }
    cursor += 11;
  }

  const shift = offset % options.length;
  return [...options.slice(shift), ...options.slice(0, shift)];
}

function getPhotoPlayers(teams: FifaTeam[]): FifaPlayer[] {
  return teams.flatMap((team) => team.players).filter((player) => player.photoUrl);
}

function getFeaturedPhotoPlayer(team: FifaTeam, offset = 0): FifaPlayer | undefined {
  const photoPlayers = team.players.filter((player) => player.photoUrl);
  return photoPlayers[offset % photoPlayers.length];
}

function toStaticQuestion(question: SeedQuestion, index: number): QuizQuestion | null {
  const correctIndex = question.options.indexOf(question.answer);
  if (correctIndex < 0) {
    return null;
  }

  return {
    id: question.id,
    category: formatCategory(question.category),
    difficulty: formatDifficulty(question.difficulty),
    prompt: question.prompt,
    options: question.options,
    correctIndex,
    explanation: question.explanation,
    sourceType: question.sourceType,
    visual: {
      label: question.category === "football_laws" ? "LAW" : question.category === "world_cup_history" ? "WC" : "2026",
      sublabel: formatCategory(question.category),
      accent: accentByCategory[question.category] ?? (index % 2 === 0 ? "blue" : "gold"),
      showCard: false
    }
  };
}

function buildPhotoNameQuestions(teams: FifaTeam[]): QuizQuestion[] {
  const allPhotoPlayers = getPhotoPlayers(teams);

  return teams.flatMap((team, index) => {
    const player = getFeaturedPhotoPlayer(team, index);
    if (!player?.photoUrl) {
      return [];
    }

    const optionPlayers = playerOptionSet(player, allPhotoPlayers, index * 7);
    const options = optionPlayers.map((optionPlayer) => optionPlayer.name);

    return [
      {
        id: `fifa-photo-name-${team.slug}`,
        category: "Players",
        difficulty: index % 4 === 0 ? "Hard" : "Medium",
        prompt: "Who is this 2026 World Cup squad player?",
        options,
        correctIndex: optionPlayers.findIndex((optionPlayer) => optionPlayer.id === player.id),
        explanation: `${player.name} is listed in FIFA's ${player.teamName} squad page.`,
        imageUrl: player.photoUrl,
        imageAlt: "Mystery player photo",
        sourceType: player.sourceType,
        sourceUrl: player.sourceUrl,
        visual: {
          label: "Mystery Player",
          sublabel: "Name the player",
          accent: "blue",
          showCard: false
        }
      }
    ];
  });
}

function buildNamePhotoQuestions(teams: FifaTeam[]): QuizQuestion[] {
  const allPhotoPlayers = getPhotoPlayers(teams);

  return teams.flatMap((team, index) => {
    const player = getFeaturedPhotoPlayer(team, index + 2);
    if (!player?.photoUrl) {
      return [];
    }

    const optionPlayers = playerOptionSet(player, allPhotoPlayers, index * 13);
    const options = optionPlayers.map((optionPlayer) => optionPlayer.name);

    return [
      {
        id: `fifa-name-photo-${team.slug}`,
        category: "Players",
        difficulty: "Hard",
        prompt: `Which photo shows ${player.name}?`,
        options,
        correctIndex: optionPlayers.findIndex((optionPlayer) => optionPlayer.id === player.id),
        explanation: `${player.name} is listed in FIFA's ${player.teamName} squad page.`,
        optionImages: optionPlayers.map((optionPlayer, optionIndex) => ({
          imageUrl: optionPlayer.photoUrl ?? "",
          imageAlt: `Option ${String.fromCharCode(65 + optionIndex)} player photo`
        })),
        sourceType: player.sourceType,
        sourceUrl: player.sourceUrl,
        visual: {
          label: "Find the Photo",
          sublabel: "Pick the face",
          accent: "blue",
          showCard: false
        }
      }
    ];
  });
}

function buildPlayerTeamQuestions(teams: FifaTeam[]): QuizQuestion[] {
  const teamNames = teams.map((team) => team.name);

  return teams.map((team, index) => {
    const featuredPlayer =
      team.players.find((player) => player.position === "Forward" && player.photoUrl) ??
      team.players.find((player) => player.photoUrl) ??
      team.players[0];
    const options = optionSet(team.name, teamNames, index * 5);

    return {
      id: `fifa-player-team-${team.slug}`,
      category: "Players",
      difficulty: index % 3 === 0 ? "Hard" : "Medium",
      prompt: `Which 2026 World Cup squad is ${featuredPlayer.name} listed with?`,
      options,
      correctIndex: options.indexOf(team.name),
      explanation: `${featuredPlayer.name} is listed in FIFA's ${team.name} squad page.`,
      imageUrl: featuredPlayer.photoUrl ?? undefined,
      imageAlt: featuredPlayer.photoAlt,
      sourceType: featuredPlayer.sourceType,
      sourceUrl: featuredPlayer.sourceUrl,
      visual: {
        label: featuredPlayer.name,
        sublabel: "World Cup 2026",
        accent: "blue",
        showCard: false
      }
    };
  });
}

function buildPositionQuestions(teams: FifaTeam[]): QuizQuestion[] {
  const positionOptions = ["Goalkeeper", "Defender", "Midfielder", "Forward"];
  const players = teams.flatMap((team) => team.players);
  const sampledPlayers = players
    .filter((player) => positionOptions.includes(player.position))
    .filter((player) => player.photoUrl)
    .filter((_, index) => index % 37 === 0)
    .slice(0, 32);

  return sampledPlayers.map((player, index) => {
    const options = optionSet(player.position, positionOptions, index);

    return {
      id: `fifa-player-position-${player.teamSlug}-${index + 1}`,
      category: "Positions",
      difficulty: "Medium",
      prompt: `According to FIFA's squad page, what is ${player.name}'s listed position?`,
      options,
      correctIndex: options.indexOf(player.position),
      explanation: `${player.name} is listed as a ${player.position} for ${player.teamName}.`,
      imageUrl: player.photoUrl ?? undefined,
      imageAlt: player.photoAlt,
      sourceType: player.sourceType,
      sourceUrl: player.sourceUrl,
      visual: {
        label: player.name,
        sublabel: player.teamName,
        accent: "green",
        showCard: false
      }
    };
  });
}

const staticQuestions = (rawQuestionBank.questions as SeedQuestion[])
  .filter(isProductionReady)
  .map(toStaticQuestion)
  .filter((question): question is QuizQuestion => Boolean(question));

function buildQuestionBank(rawSquads: FifaSquads): QuizQuestion[] {
  const teams = rawSquads.teams.filter((team) => team.players.length > 0);

  return [
    ...buildPhotoNameQuestions(teams),
    ...buildNamePhotoQuestions(teams),
    ...buildPlayerTeamQuestions(teams),
    ...staticQuestions,
    ...buildPositionQuestions(teams)
  ];
}

export const staticQuestionBank: QuizQuestion[] = staticQuestions;

export const questionBank = staticQuestionBank;

export const seedQuestions = staticQuestionBank;

export async function loadQuestionBank(): Promise<QuizQuestion[]> {
  if (cachedQuestionBank) {
    return cachedQuestionBank;
  }

  const response = await fetch("/data/fifa-world-cup-2026-squads.json");
  if (!response.ok) {
    throw new Error(`Failed to load FIFA squad data: ${response.status}`);
  }

  cachedQuestionBank = buildQuestionBank((await response.json()) as FifaSquads);
  return cachedQuestionBank;
}

export function getQuestionById(questionId: string, questions: QuizQuestion[] = staticQuestionBank): QuizQuestion | undefined {
  return questions.find((question) => question.id === questionId);
}

export function pickQuizQuestions(count: number, questions: QuizQuestion[] = staticQuestionBank): QuizQuestion[] {
  const shuffled = [...questions];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled.slice(0, count);
}
